<script lang="ts">
	import InputField from '../molecules/InputField.svelte'
	import { inputFieldFormats } from '$utils/formatters'
	import { debounce } from '$utils/utils'
	import type { FontProps } from '$types/fontProps'
	import { DefaultRobotoFontProps } from '$types/fontProps'
	import { observeTableVisibility } from '$utils/intersectionObserver'
	import type { AnalyticalValue } from './types/analyticalValue'
	import { t } from '$i18n/i18n'
	import tippy, { type Instance } from 'tippy.js'

	const tooltipContent = `
        <div class="custom-test">
            <h3 style="line-height: 1.25rem;">${$t('Analyticals.input.tooltip.title')}</h3>
            <div style="display: grid; grid-template-columns: 50px 1fr; font-size: 0.875rem; line-height: 1.25rem;">
                <span style="color: #58C078; font-size: 0.875rem;">${$t('Analyticals.input.tooltip.nn')}</span>
                <span style="font-size: 0.875rem;">${$t('Analyticals.input.tooltip.nn.desc')}</span>
                <span style="color: #58C078; font-size: 0.875rem;">${$t('Analyticals.input.tooltip.ltValue')}</span>
                <span style="font-size: 0.875rem;">${$t('Analyticals.input.tooltip.ltValue.desc')}</span>
                <span style="color: #58C078; font-size: 0.875rem;">${$t('Analyticals.input.tooltip.gtValue')}</span>
                <span style="font-size: 0.875rem;">${$t('Analyticals.input.tooltip.gtValue.desc')}</span>
                <span style="color: #58C078; font-size: 0.875rem;">${$t('Analyticals.input.tooltip.ng')}</span>
                <span style="font-size: 0.875rem;">${$t('Analyticals.input.tooltip.ng.desc')}</span>
            </div>
        </div>
        `

	let analysisTippy: Instance | undefined

	function setupAnalysisTippy(node: HTMLElement) {
    // We use tippy.js here instead of svelte-tippy, since we want to set the trigger prop which is omitted in svelte-tippy
		// for an unknown reason. The trigger prop works with svelte-tippy (because it is a valid tippy.js prop!), but we
		// get a type error.
		analysisTippy = tippy(node, {
			animation: 'scale-extreme',
			allowHTML: true,
			content: tooltipContent,
			delay: [100, 200],
			hideOnClick: true,
			interactive: true,
			maxWidth: 375,
			offset: [-1, 13],
			placement: 'top',
			theme: 'mmttip',
			trigger: 'focusin',
			appendTo: document.body
		})

		return {
			destroy() {
				analysisTippy?.destroy()
			}
		}
	}

	// type Item = $$Generic;
	export let addonText: string = ''
	export let label: string = ''
	export let ttText: string = ''
	export let format = inputFieldFormats.NORMAL
	export let placeholder: string = ''
	export let isNumeric: boolean = false
	export let unit: string = ''
	export let forcedDecimalPlaces: number = 0

	export let editable: boolean = true
	export let row /*: BodyRow<Item>*/
	export let column /*: DataColumn<Item>*/
	export let value /*: unknown;*/
	export let onUpdateValue /*: (rowDataId: string, columnId: string, newValue: unknown) => void*/
	export let analyticalValue: AnalyticalValue

	let editingValue: number

	let fontProps: FontProps = DefaultRobotoFontProps('0.875rem', '0.75rem')

	const handleSubmit = () => {
		if (row.isData()) {
			onUpdateValue(row.dataId, column.id, editingValue)
		}
	}

	const handleFocus = () => {
		window.dispatchEvent(
			new CustomEvent('focus-input', {
				detail: {
					key: analyticalValue.key
				}
			})
		)
	}

	$: editingValue = value
</script>

<!-- for logging use:observeTableVisibility={true} -->
<div
	id={'cell-' + analyticalValue.key}
	style="width: fit-content;"
	use:setupAnalysisTippy
	use:observeTableVisibility
>
	<!-- add the container-width to the input field, so that the tooltip is displayed in the middle of the input field -->
	<InputField
		bind:value={editingValue}
		id="{'input-' + analyticalValue.key}{label}"
		{addonText}
		{ttText}
		{isNumeric}
		{format}
		{placeholder}
		{unit}
		{fontProps}
		{forcedDecimalPlaces}
		errorHighlight={analyticalValue.valueError ? analyticalValue.valueError !== 'NONE' : false}
		readOnly={!editable}
		containerWidth={format.maxWidth}
		on:input={debounce(handleSubmit)}
		on:focus={handleFocus}
	/>
</div>
